// src/components/PedidosListener.tsx
import React, { useEffect, useRef } from 'react';
import { Pedido } from '../../types';
import { toast } from 'react-toastify';
import { clearScope } from '../../../../../crud/cache/cachePublicMethods';
import { notificationSoundManager } from '../../../../../utils/notificationSound';

const WS_URL = 'wss://p3lb9s35ke.execute-api.us-east-2.amazonaws.com/production/';

interface PedidosListenerProps {
  setPedidos: React.Dispatch<React.SetStateAction<Pedido[]>>;
  pedido_type_id: number;
  updateSalones?: (mesaId: number, payload: any) => void;
}

const PedidosListener: React.FC<PedidosListenerProps> = ({
  setPedidos,
  pedido_type_id,
  updateSalones,
}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<number>();
  const heartbeatRef = useRef<number>();

  // Keep stable refs to your updaters
  const setPedidosRef = useRef(setPedidos);
  setPedidosRef.current = setPedidos;
  const updateSalonesRef = useRef(updateSalones);
  updateSalonesRef.current = updateSalones;

  // These truly never change
  const deviceId = React.useMemo(() => {
    const key = 'pedidosDeviceId';
    const stored = localStorage.getItem(key);
    if (stored) return stored;
    const id = crypto.randomUUID();
    localStorage.setItem(key, id);
    return id;
  }, []);

  const localId = React.useMemo(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.local_id ?? 0;
  }, []);

  useEffect(() => {
    let shouldReconnect = true;

    const startHeartbeat = () => {
      heartbeatRef.current = window.setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          //console.log('WebSocket heartbeat: connected');
        } else {
          //console.error('WebSocket heartbeat: disconnected');
        }
      }, 1000);
    };
    const stopHeartbeat = () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };

    const connect = () => {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        //console.log('WebSocket opened');
        startHeartbeat();
        ws.send(JSON.stringify({
          action: 'handler',
          data: { device_id: deviceId, local_id: localId, type: 'saveSocket' },
        }));
      };

      ws.onmessage = evt => {
        let msg: any;
        try { msg = JSON.parse(evt.data); }
        catch { return; }

        switch (msg.event) {

          case 'comandas_ready':
            // extract pedido_id and update list
            {
              const pedidoId = msg.data.pedido_id as number;
              let matched: Pedido | undefined;
              setPedidosRef.current(prev => {
                matched = prev.find(p => p.pedido_id === pedidoId);
                return prev.map(p => p.pedido_id === pedidoId ? { ...p, isReady: true } : p);
              });

              // Play notification sound for pedidos topic
              notificationSoundManager.playNotificationSound('pedidos');

              // If we have the pedido details from prev state, show a human-friendly toast
              if (matched) {
                const mesa = matched.mesa_num ?? matched.mesa_id ?? matched.pedido_id;
                const user = matched.user_name ?? 'Sin asignar';
                const items = Array.isArray(matched.selections) ? matched.selections : [];
                const top = items.slice(0, 2).map(s => `${s.quantity} ${s.item_name}`).join(', ');
                const extra = items.length > 2 ? ` +${items.length - 2} más` : '';
                const resumen = top ? ` — ${top}${extra}` : '';
                toast.success(`Pedido listo: Mesa ${mesa} · ${user}${resumen}`);
              } else {
                // Fallback toast when we don't yet have the pedido in local state
                toast.success(`Pedido #${pedidoId} está listo`);
              }
            }
            break;

          case 'new_pedido':
            if (msg.data?.pedido_type_id === pedido_type_id) {
              setPedidosRef.current(prev =>
                prev.some(p => p.pedido_id === msg.data.pedido_id)
                  ? prev
                  : [...prev, msg.data as Pedido]
              );
              
              // Play notification sound for pedidos topic
              notificationSoundManager.playNotificationSound('pedidos');
              
              // Show toast notification for new pedido
              if (pedido_type_id !==1 && pedido_type_id!==5) {
              const clientName = msg.data.client_name ?? 'Cliente sin nombre';
              const pedidoType = msg.data.third_party_provider_id ? 'Delivery' : 'Pickup';
              toast.info(`Nuevo pedido ${pedidoType}: ${clientName}`);
              }
            }
            break;

          case 'table_opened':
            // Clear salones cache to ensure fresh layout/status
            void clearScope({ namespace: 'management_api', group: 'salones' });
            updateSalonesRef.current?.(msg.data.mesa_id, msg);
            toast.info(`${msg.data.user_name ?? 'Alguien'} tomó ${msg.data.mesa_id ? 'la mesa' : 'el grupo'} ${msg.data.mesa_number ?? msg.data.mesa_id}`);
            break;

          case 'table_closed':
            // Clear salones cache to ensure fresh layout/status
            void clearScope({ namespace: 'management_api', group: 'salones' });
            updateSalonesRef.current?.(msg.data.mesa_id, msg);
            toast.info(`${msg.data.mesa_id ? 'La mesa' : 'El grupo'} ${msg.data.mesa_number ?? msg.data.mesa_id} fue ${msg.data.mesa_id ? 'cerrada' : 'cobrado'}`);
            break;

          case 'error':
            console.error('WebSocket error event:', msg.data);
            break;

          default:
            break;
        }
      };

      ws.onerror = e => {
        console.error('WebSocket encountered error:', e);
        ws.close();
      };

      ws.onclose = evt => {
        console.error('WebSocket closed:', evt.reason);
        stopHeartbeat();
        if (shouldReconnect) {
          retryRef.current = window.setTimeout(connect, 5000);
        }
      };
    };

    connect();
    return () => {
      shouldReconnect = false;
      if (retryRef.current) clearTimeout(retryRef.current);
      stopHeartbeat();
      wsRef.current?.close();
    };
  }, [deviceId, localId, pedido_type_id]); // ← Only these

  return null;
};

export default PedidosListener;
